import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { ClipboardList, Loader2, RefreshCw, Sparkles, ShieldAlert, ListChecks } from "lucide-react";
import { toast } from "sonner";

/**
 * Pack 3 · Deliverable 4 — AI Meeting Brief™.
 *
 * Consultant-only. Displays the AI-generated meeting brief for a lead.
 * Auto-fetches on open; regenerates on demand via `?regenerate=1`.
 */
export default function MeetingBriefSheet({ open, lead, onOpenChange }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const fetchBrief = useCallback(async (regenerate = false) => {
    if (regenerate) setRegenerating(true); else setLoading(true);
    try {
      const { data } = await api.get(
        `/leads/${lead.id}/meeting-brief${regenerate ? "?regenerate=true" : ""}`,
        { timeout: 120000 },
      );
      setBrief(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load meeting brief");
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  }, [lead?.id]);

  useEffect(() => {
    if (!open || !lead?.id) return;
    setBrief(null);
    fetchBrief(false);
  }, [open, lead?.id, fetchBrief]);

  const mb = brief?.meeting_brief;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="meeting-brief-sheet">
        <SheetHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <SheetTitle className="font-display text-2xl">
                AI Meeting Brief™ · <span className="text-accent">{lead?.company || lead?.name}</span>
              </SheetTitle>
              <SheetDescription>
                {lead?.country} · {lead?.industry} · {lead?.function} · Score {lead?.results?.overall_score ?? "—"}/100
              </SheetDescription>
            </div>
            {mb && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchBrief(true)}
                disabled={regenerating}
                data-testid="brief-regen-btn"
                title="Regenerate the brief"
              >
                {regenerating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                Regenerate
              </Button>
            )}
          </div>
        </SheetHeader>

        {loading ? (
          <div className="py-20 flex flex-col items-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
            <div className="mt-3 text-sm">Preparing your meeting brief…</div>
            <div className="text-xs mt-1">This can take up to 30 seconds on first load.</div>
          </div>
        ) : !mb ? (
          <div className="py-20 text-center text-sm text-muted-foreground" data-testid="brief-empty">
            No brief yet. Approve the report to generate one automatically.
          </div>
        ) : (
          <div className="mt-5 space-y-6">
            <Block title="Executive Summary" icon={Sparkles} testId="brief-exec-summary">
              <p className="text-sm leading-relaxed">{mb.executive_summary}</p>
            </Block>

            {mb.discovery_highlights?.length ? (
              <Block title="Discovery Highlights" icon={ListChecks} testId="brief-highlights">
                <ul className="space-y-2">
                  {mb.discovery_highlights.map((h, i) => (
                    <li key={`h-${i}`} className="flex gap-2 text-sm">
                      <span className="text-accent mt-1">▍</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </Block>
            ) : null}

            {mb.top_opportunities?.length ? (
              <Block title="Top Opportunities" testId="brief-opportunities">
                <div className="grid gap-3">
                  {mb.top_opportunities.map((o, i) => (
                    <Card key={`o-${i}`} className="p-4">
                      <div className="font-sans-ui font-semibold text-sm">{o.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{o.why_it_matters}</div>
                      {o.leading_question && (
                        <div className="text-xs text-accent mt-2">
                          <span className="uppercase tracking-widest">Ask:</span> {o.leading_question}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </Block>
            ) : null}

            {mb.suggested_discovery_questions?.length ? (
              <Block title="Suggested Discovery Questions" icon={ClipboardList} testId="brief-questions">
                <ol className="list-decimal pl-5 space-y-1 text-sm marker:text-accent">
                  {mb.suggested_discovery_questions.map((q, i) => (
                    <li key={`q-${i}`}>{q}</li>
                  ))}
                </ol>
              </Block>
            ) : null}

            {mb.business_risks?.length ? (
              <Block title="Business Risks" icon={ShieldAlert} testId="brief-risks">
                <ul className="space-y-1 text-sm">
                  {mb.business_risks.map((r, i) => (
                    <li key={`r-${i}`} className="flex gap-2">
                      <span className="text-amber-300 mt-1">▍</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </Block>
            ) : null}

            {mb.meeting_agenda?.length ? (
              <Block title="Meeting Agenda" testId="brief-agenda">
                <ul className="divide-y divide-border/60 text-sm">
                  {mb.meeting_agenda.map((a, i) => (
                    <li key={`a-${i}`} className="py-2 flex justify-between gap-3">
                      <span>{a.topic}</span>
                      <span className="text-muted-foreground whitespace-nowrap">{a.minutes} min</span>
                    </li>
                  ))}
                </ul>
              </Block>
            ) : null}

            {mb.recommended_discussion_flow && (
              <Block title="Recommended Discussion Flow" testId="brief-flow">
                <p className="text-sm leading-relaxed whitespace-pre-line">{mb.recommended_discussion_flow}</p>
              </Block>
            )}

            {brief?.generated_at && (
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground text-right">
                Generated {new Date(brief.generated_at).toLocaleString()} · Language: {brief.language || "en"}
              </p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Block({ title, icon: Icon, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 overline">
        {Icon && <Icon className="w-3.5 h-3.5 text-accent" />} {title}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
